import React, { createContext, useState, useContext, useEffect } from 'react';
import { FlatCardData } from '../../components/CardsComponents/types';

interface CompareContextProps {
  compares: FlatCardData[];
  addToCompares: (card: FlatCardData) => void;
  removeFromCompares: (card: FlatCardData) => void;
  toggleCompare: (card: FlatCardData) => void;
  removeAllByType: (type: 'flat' | 'parking' | 'business') => void;
}

const CompareContext = createContext<CompareContextProps | undefined>(undefined);

export const CompareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [compares, setCompares] = useState<FlatCardData[]>(() => {
    const storedCompares = localStorage.getItem('compares');
    return storedCompares ? JSON.parse(storedCompares) : [];
  });

  useEffect(() => {
    localStorage.setItem('compares', JSON.stringify(compares));
  }, [compares]);

  const addToCompares = (card: FlatCardData) => {
    setCompares((prevCompares) => {
      if (!prevCompares.some((item) => item.id === card.id && item.type === card.type)) {
        return [...prevCompares, card];
      }
      return prevCompares;
    });
  };

  const removeFromCompares = (card: FlatCardData) => {
    setCompares((prevCompares) =>
      prevCompares.filter((item) => item.id !== card.id || item.type !== card.type)
    );
  };

  const toggleCompare = (card: FlatCardData) => {
    setCompares((prevCompares) => {
      const isCompare = prevCompares.some((item) => item.id === card.id && item.type === card.type);
      if (isCompare) {
        return prevCompares.filter((item) => item.id !== card.id || item.type !== card.type);
      } else {
        return [...prevCompares, card];
      }
    });
  };

  const removeAllByType = (type: 'flat' | 'parking' | 'business') => {
    setCompares((prevCompares) =>
      prevCompares.filter((item) => item.type !== type)
    );
  };

  return (
    <CompareContext.Provider
      value={{
        compares,
        addToCompares,
        removeFromCompares,
        toggleCompare,
        removeAllByType,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};
